import React, { memo, useEffect, useRef, useState } from 'react'

import { CSSTransition } from 'react-transition-group'
import { motion, useAnimation } from 'framer-motion'

import styles from './styles.module.scss'
import '../filesGroupAnimation.scss'
import cn from 'classnames'
import useMediaQuery from '@mui/material/useMediaQuery'

import { ReactComponent as TrashIcon } from '../../../assets/icons/trashIcon.svg'
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg'
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-white.svg'
import { ReactComponent as DeleteFileIcon } from '../../../assets/icons/close.svg'

import DateExpiredChecker from '../../../UI/DateExpiredChecker/DateExpiredChecker'
import Tooltip from '../../../UI/Tooltip/Tooltip'
import ImageComponent from '../../../UI/Image/ImageComponent'
import PhotoGalleryModal from '../../../UI/PhotoGalleryModal/PhotoGalleryModal'

const DocumentGroupItem = memo(({ type, data, onDelete, onEdit, deleteFile, isActive, isSwiped, onSwipeChange }) => {
  const [visibleListOfFiles, setVisibleListOfFiles] = useState(false)
  const [visibleListOfEndorsementFiles, setVisibleListOfEndorsementFiles] = useState(false)
  const [uncontrolledIsSwiped, setUncontrolledIsSwiped] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [activeGalleryType, setActiveGalleryType] = useState('main')
  const isTabletOrMobile = useMediaQuery('(max-width: 1200px)')
  const isSwipedValue = typeof isSwiped === 'boolean' ? isSwiped : uncontrolledIsSwiped

  const swipeRef = useRef({
    startX: 0,
    startY: 0,
    pointerId: null,
    isTracking: false,
    isHorizontal: false,
  })

  const nodeRef = useRef(null)
  const control = useAnimation()

  useEffect(() => {
    let isMounted = true

    const animateControl = () => {
      if (visibleListOfFiles || visibleListOfEndorsementFiles) {
        control.start((i) => ({
          opacity: 1,
          x: 0,
          transition: { delay: i * 0.1, type: 'spring' },
        }))
      } else {
        control.start((i) => ({
          opacity: 0,
          x: 0,
          transition: { delay: i * 0.1, type: 'spring' },
        }))
      }
    }

    if (isMounted) animateControl()

    return () => {
      isMounted = false
    }
  }, [visibleListOfFiles, visibleListOfEndorsementFiles, control])

  const handleFilesEnter = () => {
    setVisibleListOfFiles(true)
  }
  const handleFilesLeave = () => {
    setVisibleListOfFiles(false)
  }

  const handleFilesEndorsementEnter = () => {
    setVisibleListOfEndorsementFiles(true)
  }
  const handleFilesEndorsementLeave = () => {
    setVisibleListOfEndorsementFiles(false)
  }

  const openGallery = (galleryType = 'main', index = 0) => {
    setActiveGalleryType(galleryType)
    setActiveIndex(index)
    setIsGalleryOpen(true)
  }

  const closeGallery = () => {
    setIsGalleryOpen(false)
  }

  const isInteractiveTarget = (target) => {
    if (!target?.closest) return false
    return Boolean(target.closest('button, a, input, label, textarea, select'))
  }

  const handlePointerDown = (e) => {
    if (isInteractiveTarget(e.target)) return

    swipeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      isTracking: true,
      isHorizontal: false,
    }

    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (_) {
      // ignore
    }
  }

  const handlePointerMove = (e) => {
    const st = swipeRef.current
    if (!st.isTracking || st.pointerId !== e.pointerId) return

    const dx = e.clientX - st.startX
    const dy = e.clientY - st.startY

    if (!st.isHorizontal) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      st.isHorizontal = Math.abs(dx) > Math.abs(dy)
    }

    if (st.isHorizontal) {
      e.preventDefault()
    }
  }

  const handlePointerEnd = (e) => {
    const st = swipeRef.current
    if (!st.isTracking || st.pointerId !== e.pointerId) return

    const dx = e.clientX - st.startX
    const dy = e.clientY - st.startY

    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx < -40) {
        if (typeof onSwipeChange === 'function') onSwipeChange(true)
        else setUncontrolledIsSwiped(true)
      }
      if (dx > 30) {
        if (typeof onSwipeChange === 'function') onSwipeChange(false)
        else setUncontrolledIsSwiped(false)
      }
    }

    swipeRef.current.isTracking = false
    swipeRef.current.pointerId = null
  }

  return (
    <div
      className={cn(styles.card, { [styles.card__active]: isActive, [styles.swiped]: isSwipedValue })}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <div className={styles.cardInfo}>
        <div className={styles.documentType}>{data.documentName}</div>

        <div className={styles.documentBody}>
          <div className={styles.documentMainData}>
            <div className={styles.documentNumbers}>
              {data?.number != null && data.number !== '' && data.number !== 'null' && (
                <p className={styles.numberRow}>
                  <span className={styles.documentNumber}>{type === 'license' ? 'License number:' : 'Number:'}</span>
                  {data?.is_gdpr && (type === 'passport' || type === 'certificate') ? (
                    <Tooltip
                      wrapperClassName={styles.gdprTooltipInlineWrapper}
                      symbolClassName={styles.gdprTooltipInlineSymbol}
                      symbol={<span className={styles.numberValue}>{data.number}</span>}
                    >
                      {type === 'passport'
                        ? 'We do not store or process full passport numbers. For your security, sensitive data is masked.'
                        : 'We do not store or process sensitive medical certificate numbers. For your security, sensitive data is masked.'}
                    </Tooltip>
                  ) : (
                    <span className={styles.numberValue}>{data.number}</span>
                  )}
                </p>
              )}

              <div className={styles.periods}>
                <div className={styles.period}>
                  <span className={styles.period_title}>Period:</span>

                  {data.date_of_issue && (
                    <span className={styles.periodValue}>
                      {data.date_of_issue}-<DateExpiredChecker>{data.date_of_expire}</DateExpiredChecker>
                    </span>
                  )}

                  {!data.date_of_issue && !data.date_of_expire && <span className={styles.periodValue}>No info</span>}
                </div>
              </div>
            </div>

            {data?.endorsement && (
              <div className={styles.documentNumbers}>
                <p>
                  <span className={styles.documentNumber}>Endorsement number:</span>
                  <span className={styles.numberValue}>{data?.endorsement?.number}</span>
                </p>

                <div className={styles.period}>
                  <span className={styles.period_title}>Period:</span>
                  <span className={styles.periodValue}>{data.endorsement?.date_of_issue}</span>-
                  <DateExpiredChecker>{data?.endorsement?.date_of_expire}</DateExpiredChecker>
                </div>
              </div>
            )}
          </div>

          <div className={styles.files}>
            {data?.files?.length ? (
              <div
                key={data.files[0].id}
                className={styles.file}
                onMouseEnter={!isTabletOrMobile ? handleFilesEnter : undefined}
              >
                <div key={data.files[0].id} className={styles.file}>
                  {data.files.length > 1 && (
                    <div className={styles.calcOfFiles}>
                      <span className={styles.calcOfFilesLabel}>{data.files.length}</span>
                    </div>
                  )}

                  <a
                    rel='noopener noreferrer'
                    target='_blank'
                    href={data.files[0].file}
                    onClick={(e) => {
                      if (!isTabletOrMobile) return
                      e.preventDefault()
                      openGallery('main', 0)
                    }}
                  >
                    <ImageComponent src={data.files[0].file_mini} alt='file' />
                  </a>
                </div>
              </div>
            ) : (
              <div className={styles.file} />
            )}

            {data?.endorsement?.files?.length ? (
              <div
                key={data.endorsement.files[0].id}
                className={styles.file}
                onMouseEnter={!isTabletOrMobile ? handleFilesEndorsementEnter : undefined}
              >
                {data.endorsement.files.length > 1 && (
                  <div className={styles.calcOfFiles}>
                    <span className={styles.calcOfFilesLabel}>{data.endorsement.files.length}</span>
                  </div>
                )}

                <a
                  rel='noopener noreferrer'
                  target='_blank'
                  href={data.endorsement.files[0].file}
                  onClick={(e) => {
                    if (!isTabletOrMobile) return
                    e.preventDefault()
                    openGallery('endorsement', 0)
                  }}
                >
                  <ImageComponent src={data.endorsement.files[0].file_mini} alt='file' />
                </a>
              </div>
            ) : (
              ''
            )}
          </div>

          {!isTabletOrMobile && (
            <CSSTransition
              nodeRef={nodeRef}
              in={visibleListOfFiles}
              timeout={500}
              unmountOnExit
              classNames='listOfFiles'
            >
              <div ref={nodeRef} className='listOfFiles' onMouseLeave={handleFilesLeave}>
                <div className={styles.files_list}>
                  {data?.files?.length &&
                    data.files.map((file) => (
                      <motion.div
                        key={file.id}
                        className={styles.file}
                        initial={{ opacity: 0, x: 20 }}
                        animate={control}
                        custom={file.index}
                      >
                        <div key={file.id} className={styles.file}>
                          <div
                            className={styles.deleteFile}
                            onClick={() => deleteFile(file.id, data.id, type, isActive)}
                          >
                            <DeleteFileIcon />
                          </div>

                          <a rel='noopener noreferrer' target='_blank' href={file.file}>
                            <ImageComponent src={file.file_mini} alt='file' />
                          </a>
                        </div>
                      </motion.div>
                    ))}
                </div>
              </div>
            </CSSTransition>
          )}

          {!isTabletOrMobile && (
            <CSSTransition
              nodeRef={nodeRef}
              in={visibleListOfEndorsementFiles}
              timeout={500}
              unmountOnExit
              classNames='listOfFiles'
            >
              <div ref={nodeRef} className='listOfFiles' onMouseLeave={handleFilesEndorsementLeave}>
                <div className={styles.files_list_endorsement}>
                  {data?.endorsement?.files?.length
                    ? data.endorsement.files.map((file) => (
                        <motion.div
                          key={file.id}
                          className={styles.file}
                          custom={file.index}
                          initial={{ opacity: 0, x: 20 }}
                          animate={control}
                        >
                          <div key={file.id} className={styles.file}>
                            <div
                              className={styles.deleteFile}
                              onClick={() => deleteFile(file.id, data.id, 'endorsement', isActive)}
                            >
                              <DeleteFileIcon />
                            </div>

                            <a rel='noopener noreferrer' target='_blank' href={file.file}>
                              <ImageComponent src={file.file_mini} alt='file' />
                            </a>
                          </div>
                        </motion.div>
                      ))
                    : ''}
                </div>
              </div>
            </CSSTransition>
          )}
        </div>
      </div>

      <PhotoGalleryModal
        open={Boolean(isGalleryOpen && isTabletOrMobile)}
        title={data.documentName}
        files={activeGalleryType === 'endorsement' ? data?.endorsement?.files || [] : data?.files || []}
        initialIndex={activeIndex}
        onClose={closeGallery}
        onRequestDelete={(file) =>
          deleteFile(file.id, data.id, activeGalleryType === 'endorsement' ? 'endorsement' : type, isActive)
        }
      />

      {data.id !== 'default' && (
        <div className={styles.cardControls}>
          <button
            className={cn(styles.cardControl, styles.cardControlEdit)}
            onClick={() => onEdit(data.id, !isActive)}
            type='button'
          >
            {isActive ? <CloseIcon /> : <EditIcon />}
          </button>

          <button
            className={cn(styles.cardControl, styles.cardControlDelete)}
            onClick={() => onDelete(data.id, isActive)}
            type='button'
          >
            <TrashIcon />
          </button>
        </div>
      )}
    </div>
  )
})

export default DocumentGroupItem
